import React, { useMemo, useState } from 'react'
import { Printer, Download, Search, FileText } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { StatusBadge } from '@/components/ui/StatusBadge'
import { formatVnNumber } from '@/features/purchasing/poWorkflow'
import { formatDateFull } from '@/lib/dateFormat'
import {
  DETAILED_REPORT_TITLES,
  type DetailedReportType,
  type DetailedReportsData,
} from '@/services/reports/inventoryDashboardApi'

export interface DetailedReportsTabProps {
  data: DetailedReportsData
  selectedReportType: DetailedReportType
  onSelectReportType: (type: DetailedReportType) => void
  loading?: boolean
}

function getStatusBadgeConfig(val: unknown): { label: string; tone: 'neutral' | 'success' | 'warning' | 'danger' | 'info' } | null {
  if (typeof val !== 'string') return null
  const upper = val.trim().toUpperCase()
  switch (upper) {
    case 'ACTIVE':
      return { label: 'Đang giữ', tone: 'info' }
    case 'RELEASED':
      return { label: 'Đã giải phóng', tone: 'neutral' }
    case 'CONSUMED':
      return { label: 'Đã xuất', tone: 'success' }
    case 'MANUAL_HOLD':
      return { label: 'Giữ thủ công', tone: 'warning' }
    case 'PRODUCTION':
      return { label: 'Lệnh sản xuất', tone: 'info' }
    case 'PURCHASING':
      return { label: 'Mua hàng', tone: 'neutral' }
    case 'PRODUCTION_MAKEUP':
      return { label: 'Bổ sung sản xuất', tone: 'warning' }
    default:
      return null
  }
}
export function DetailedReportsTab({
  data,
  selectedReportType,
  onSelectReportType,
  loading,
}: DetailedReportsTabProps): React.ReactElement {
  const [searchTerm, setSearchTerm] = useState('')

  // Filter rows locally if searched
  const filteredRows = useMemo(() => {
    if (!searchTerm.trim()) return data.rows
    const term = searchTerm.toLowerCase()
    return data.rows.filter((row) =>
      Object.values(row).some(
        (val) => val != null && String(val).toLowerCase().includes(term),
      ),
    )
  }, [data.rows, searchTerm])

  // Export CSV handler
  function handleExportCsv() {
    if (!data.columns.length || !filteredRows.length) return

    // Header row
    const headers = ['STT', ...data.columns.map((c) => `"${c.label.replace(/"/g, '""')}"`)]

    // Data rows
    const csvRows = filteredRows.map((row, idx) => {
      const line = [
        String(idx + 1),
        ...data.columns.map((col) => {
          const val = row[col.key]
          if (val == null) return '""'
          const badgeConfig = getStatusBadgeConfig(val)
          const formatted =
            typeof val === 'number'
              ? String(val)
              : badgeConfig
              ? badgeConfig.label
              : String(val)
          return `"${formatted.replace(/"/g, '""')}"`
        }),
      ]
      return line.join(',')
    })

    // UTF-8 BOM for Excel compatibility
    const csvContent = '\uFEFF' + [headers.join(','), ...csvRows].join('\r\n')
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = `${selectedReportType}_${new Date().toISOString().slice(0, 10)}.csv`
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(url)
  }

  // Print handler
  function handlePrint() {
    window.print()
  }

  return (
    <div className="space-y-4">
      {/* Top Filter and Actions Toolbar */}
      <div className="flex flex-col gap-3 rounded-[var(--radius-lg)] border border-[var(--color-border)] bg-white p-3.5 shadow-[var(--shadow-level-1)] sm:flex-row sm:items-center sm:justify-between">
        {/* Left: Dropdown select report type */}
        <div className="flex flex-col gap-1.5 sm:flex-row sm:items-center">
          <label
            htmlFor="report-type-select"
            className="text-xs font-semibold uppercase text-[var(--color-text-muted)] whitespace-nowrap"
          >
            Loại báo cáo:
          </label>
          <select
            id="report-type-select"
            value={selectedReportType}
            onChange={(e) => onSelectReportType(e.target.value as DetailedReportType)}
            className="h-9 rounded border border-[var(--color-border)] bg-white px-3 text-sm font-medium text-slate-800 focus:border-[var(--color-accent)] focus:outline-hidden"
          >
            {(Object.keys(DETAILED_REPORT_TITLES) as DetailedReportType[]).map((type) => (
              <option key={type} value={type}>
                {DETAILED_REPORT_TITLES[type]}
              </option>
            ))}
          </select>
        </div>

        {/* Middle: Search Box */}
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-[var(--color-text-muted)]" />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Tìm theo mã, tên, người lập..."
            className="h-9 w-full rounded border border-[var(--color-border)] bg-white pl-8 pr-3 text-sm focus:border-[var(--color-accent)] focus:outline-hidden"
          />
        </div>

        {/* Right: Export & Print Buttons */}
        <div className="flex items-center gap-2">
          <Button variant="secondary" size="sm" onClick={handleExportCsv} title="Xuất file CSV (Excel)">
            <Download className="h-4 w-4" />
            <span className="hidden sm:inline">Xuất CSV</span>
          </Button>
          <Button variant="secondary" size="sm" onClick={handlePrint} title="In báo cáo">
            <Printer className="h-4 w-4" />
            <span className="hidden sm:inline">In báo cáo</span>
          </Button>
        </div>
      </div>

      {/* Report Container */}
      <div className="rounded-[var(--radius-lg)] border border-[var(--color-border)] bg-white p-4 shadow-[var(--shadow-level-1)]">
        {/* Report Header */}
        <div className="mb-3 flex items-center justify-between border-b border-slate-100 pb-3">
          <div className="flex items-center gap-2">
            <div className="flex h-7 w-7 items-center justify-center rounded-sm bg-slate-100 text-slate-700">
              <FileText className="h-4 w-4" />
            </div>
            <h3 className="text-base font-semibold text-slate-900">{data.reportTitle || data.title}</h3>
          </div>
          <div className="text-xs text-[var(--color-text-muted)]">
            Tổng cộng: <strong className="text-slate-800">{filteredRows.length}</strong> dòng dữ liệu
          </div>
        </div>

        {loading ? (
          <div className="py-12 text-center text-sm text-[var(--color-text-muted)] italic">
            Đang tải dữ liệu báo cáo...
          </div>
        ) : filteredRows.length === 0 ? (
          <div className="sticky left-0 py-12 text-center text-sm text-[var(--color-text-muted)]">
            Không có dữ liệu phù hợp với điều kiện tìm kiếm.
          </div>
        ) : (
          <div className="overflow-x-auto rounded border border-[var(--color-border)]">
            <table className="min-w-full divide-y divide-[var(--color-border)] text-left text-sm">
              <thead className="bg-slate-50 text-xs font-semibold uppercase tracking-wider text-[var(--color-text-muted)]">
                <tr>
                  {/* Cột STT bắt buộc luôn luôn ở đầu */}
                  <th scope="col" className="px-3 py-2.5 text-center w-12 sticky left-0 z-10 bg-slate-50">
                    STT
                  </th>
                  {data.columns.map((col) => {
                    const alignClass =
                      col.align === 'right'
                        ? 'text-right'
                        : col.align === 'center'
                        ? 'text-center'
                        : 'text-left'

                    return (
                      <th
                        key={col.key}
                        scope="col"
                        style={{ minWidth: col.minWidth }}
                        className={`px-3 py-2.5 ${alignClass}`}
                      >
                        {col.label}
                      </th>
                    )
                  })}
                </tr>
              </thead>
              <tbody className="divide-y divide-[var(--color-border)] bg-white">
                {filteredRows.map((row, index) => (
                  <tr key={(row.id as string) || index} className="hover:bg-slate-50/80">
                    {/* STT Index (1-based displayed index) */}
                    <td className="px-3 py-2 text-center text-xs text-[var(--color-text-muted)] sticky left-0 bg-white">
                      {index + 1}
                    </td>

                    {data.columns.map((col) => {
                      const val = row[col.key]
                      const alignClass =
                        col.align === 'right'
                          ? 'text-right'
                          : col.align === 'center'
                          ? 'text-center'
                          : 'text-left'

                      const badgeConfig = getStatusBadgeConfig(val)
                      let displayVal: React.ReactNode = String(val ?? '')
                      if (badgeConfig) {
                        displayVal = <StatusBadge tone={badgeConfig.tone} label={badgeConfig.label} />
                      } else if (typeof val === 'number') {
                        displayVal = formatVnNumber(val)
                      } else if (
                        typeof val === 'string' &&
                        col.key.includes('date') &&
                        /^\d{4}-\d{2}-\d{2}$/.test(val)
                      ) {
                        displayVal = formatDateFull(val)
                      }

                      return (
                        <td
                          key={col.key}
                          className={`px-3 py-2 text-xs sm:text-sm ${alignClass} ${
                            typeof val === 'number' ? 'tabular-nums font-medium' : ''
                          }`}
                        >
                          {displayVal}
                        </td>
                      )
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}
